import type { MessageKey } from "./vi.ts";

/** English mirror of vi.ts. Type-checked and test-checked for full key parity. */
export const en: Record<MessageKey, string> = {
  "app.name": "Yabe",
  "app.tagline": "Sell without deciding what it's worth.",
  "nav.home": "Home",
  "nav.browse": "Browse",
  "nav.sell": "Sell",
  "nav.watching": "Watching",
  "nav.me": "Me",
  "nav.notifications": "Notifications",
  "nav.settings": "Settings",
  "nav.login": "Log in",
  "nav.logout": "Log out",
  "nav.admin": "Admin",

  "common.ok": "OK",
  "common.cancel": "Cancel",
  "common.close": "Close",
  "common.save": "Save",
  "common.confirm": "Confirm",
  "common.back": "Back",
  "common.next": "Next",
  "common.done": "Done",
  "common.retry": "Retry",
  "common.copy": "Copy",
  "common.copied": "Copied",
  "common.share": "Share",
  "common.loading": "Loading…",
  "common.search": "Search",
  "common.filter": "Filters",
  "common.all": "All",
  "common.new": "New",
  "common.yes": "Yes",
  "common.no": "No",
  "common.more": "More",
  "common.less": "Less",
  "common.since": "member since {year}",
  "common.language": "Language",
  "common.theme": "Theme",
  "common.theme.system": "System",
  "common.theme.light": "Light",
  "common.theme.dark": "Dark",

  "error.generic": "Something went wrong. Try again.",
  "error.network": "Connection lost. Check your network and try again.",
  "error.not_found": "Page not found.",
  "error.unauthorized": "You need to log in.",
  "error.forbidden": "You can't do that.",
  "error.rate_limited": "Too fast. Wait a moment and try again.",
  "error.validation": "Some details aren't valid.",
  "error.invalid_phone": "That phone number isn't valid.",
  "error.invalid_code": "Wrong code.",
  "error.code_expired": "Code expired. Request a new one.",
  "error.too_many_attempts": "Too many wrong attempts. Request a new code.",
  "error.phone_blocked": "This number can't be used to register.",
  "error.invalid_handle": "Handle: lowercase letters, digits and underscore, 3–20 characters.",
  "error.handle_taken": "That handle is taken.",
  "error.handle_cooldown": "You can change your handle once every 90 days.",
  "error.reserved_handle": "That handle isn't allowed.",
  "error.invalid_invite": "Invite code is wrong or already used.",
  "error.suspended": "Account is paused.",
  "error.banned": "Account is banned.",

  "auth.phone.title": "Your phone number",
  "auth.phone.hint": "We'll text you a 6-digit code. No password.",
  "auth.phone.submit": "Send code",
  "auth.otp.title": "Enter the 6-digit code",
  "auth.otp.sent_to": "Sent to {phone}",
  "auth.otp.resend": "Resend code",
  "auth.otp.resend_in": "Resend in {seconds}s",
  "auth.otp.change_phone": "Change number",
  "auth.onboard.title": "Welcome",
  "auth.onboard.display_name": "Display name",
  "auth.onboard.handle": "Handle",
  "auth.onboard.handle_hint": "yabe.io/u/{handle}",
  "auth.onboard.terms": "I agree to the",
  "auth.onboard.terms_link": "Terms",
  "auth.onboard.prohibited_link": "Prohibited items policy",
  "auth.onboard.submit": "Get started",
  "auth.state.suspended":
    "Your account is paused after 3 strikes in 90 days. An admin will review it.",
  "auth.state.banned": "This account is banned.",
  "auth.state.deleting": "Account pending deletion. Data is anonymised after 14 days.",

  "price.rule": "Price keeps falling until someone buys. Nobody sees the floor.",
  "price.start": "Start price",
  "price.current": "Current price",
  "price.floor": "Floor price",
  "price.floor.none": "No floor — let the market decide. Good items almost never reach zero.",
  "price.floor.set": "Price stops falling at {price}. Buyers never see this number.",
  "bucket.ending_soon": "Ending soon",
  "bucket.hours": "Hours left",
  "bucket.under_day": "Under a day",
  "bucket.days": "Days left",
  "counts.watching": "{n} watching",
  "counts.alerts": "{n} alerts",
  "listing.status.draft": "Draft",
  "listing.status.scheduled": "Scheduled",
  "listing.status.active": "Live",
  "listing.status.sold_out": "Sold",
  "listing.status.expired": "Expired",
  "listing.status.cancelled": "Cancelled",
  "listing.condition.new": "New",
  "listing.condition.like_new": "Like new",
  "listing.condition.good": "Good",
  "listing.condition.fair": "Fair",
  "listing.drop_tag": "Drop",
  "listing.qty_remaining": "{n} left",
  "listing.similar": "See similar items",
  "listing.under_review": "Under review",
  "listing.starts_at_drop": "Starts {when}",
  "listing.report": "Report",

  "drop.next": "Next Drop · {when}",
  "drop.this_week": "This week's Drop",
  "drop.live_now": "Live now",
  "drop.queued": "{n} items queued",
  "drop.countdown": "{d}d {h}h {m}m",
  "drop.time_label": "Sat 20:00",
  "drop.reminder.title": "Drop starts in 1 hour",
  "drop.reminder.body": "{n} items go live at 20:00.",
  "drop.start.title": "The Drop has started",
  "drop.start.body": "{n} items just went live. Prices are falling.",

  "buy.cta": "Buy now",
  "buy.quantity": "Quantity",
  "buy.total": "Total",
  "buy.confirm.note":
    "The price may have dropped a little more by the time you confirm — you pay the lower one.",
  "buy.confirm.cta": "Confirm purchase",
  "buy.success": "Bought. Waiting for @{seller} to share contact — usually within {hours} h.",
  "buy.sold_out": "Just sold out.",
  "buy.ended": "This listing has ended.",
  "buy.own_listing": "You can't buy your own listing.",
  "buy.open_cap": "You have 3 open purchases. Finish one before buying more.",
  "buy.open_problem": "You have an unresolved problem with this seller.",
  "buy.login_required": "Log in with your phone to buy.",

  "alert.watch": "Watch",
  "alert.watching": "Watching",
  "alert.set": "Set alert",
  "alert.target": "Target {price} · {pct} to go",
  "alert.set.done": "You'll be notified when the price reaches {price}.",
  "alert.reached": "{title} reached your price: {price}. Buy before someone else does.",
  "alert.reached.short": "Reached your price",
  "alert.sheet.hint": "You'll be pinged when the price hits this number.",
  "alert.sheet.watch_only": "Just watch, no target",
  "alert.remove": "Unwatch",

  "purchase.status.pending_contact": "Awaiting contact",
  "purchase.status.contact_shared": "Contact shared",
  "purchase.status.completed": "Completed",
  "purchase.status.cancelled": "Cancelled",
  "purchase.timeline.bought": "Bought",
  "purchase.timeline.waiting": "Waiting for the seller to share contact (24 h max)",
  "purchase.timeline.shared": "Contact shared",
  "purchase.timeline.completed": "Completed",
  "purchase.usual_response": "Usually shares contact within {hours} h",
  "purchase.max_response": "24 h max",
  "purchase.deadline_left": "{hours} h left",
  "purchase.share_contact": "Share contact",
  "purchase.contact.shared":
    "Contact shared. Meet up and hand over the deal code when you receive the item.",
  "purchase.call": "Call",
  "purchase.zalo": "Message on Zalo",
  "purchase.mark_done": "Mark as done",
  "purchase.confirm_done": "Confirm done",
  "purchase.report_problem": "Report a problem",
  "purchase.cancel": "Cancel",
  "cancel.warning":
    "Cancelling adds one no-show to your account. Three in 90 days pauses your account.",
  "deal.code": "Deal code",
  "deal.code.hint": "Give this code to the seller when you receive the item.",
  "deal.enter": "Enter the deal code the buyer gives you.",
  "deal.enter.cta": "Enter deal code",
  "deal.wrong": "Wrong code. {n} attempts left.",
  "deal.locked": "5 wrong entries. Try again in 1 hour.",
  "deal.completed": "Deal completed.",
  "invite.ask": "Want to sell? Ask @{seller} for an invite",
  "invite.ask.cta": "Request invite",
  "invite.ask.sent": "Request sent.",

  "problem.title": "Report a problem",
  "problem.reason.seller_no_show": "Seller didn't show up",
  "problem.reason.item_not_as_described": "Item not as described",
  "problem.reason.buyer_no_show": "Buyer didn't show up",
  "problem.reason.payment_not_received": "Payment not received",
  "problem.reason.other": "Other",
  "problem.note": "Description",
  "problem.images": "Photos (up to 3)",
  "problem.filed": "Sent. An admin will review it.",
  "problem.reply": "Reply",
  "problem.replied": "Reply sent.",
  "problem.open_badge": "Problem open",

  "profile.sold": "Sold {n}",
  "profile.sell_through": "Sell-through {pct} (6 months)",
  "profile.share_time": "Shares contact within ~{hours} h",
  "profile.bought": "Bought {n}",
  "seller.new": "New seller",
  "buyer.reliable": "Reliable buyer",
  "buyer.new": "New",
  "buyer.noshows": "{n} no-shows in the last 90 days",
  "user.deleted": "[deleted]",
  "profile.strikes": "Active strikes",
  "profile.strike.expires": "expires {date}",
  "profile.active_listings": "Live listings",
  "profile.seller_cancels": "cancelled {n} active listings in the last 90 days",

  "sell.title": "Sell",
  "sell.step.photos": "Photos",
  "sell.step.details": "Details",
  "sell.step.location": "Location",
  "sell.step.price": "Price",
  "sell.step.time": "Timing",
  "sell.step.review": "Review",
  "sell.photos.hint": "Drag to reorder. The first photo is the cover.",
  "sell.field.title": "Title",
  "sell.field.category": "Category",
  "sell.field.condition": "Condition",
  "sell.field.description": "Description",
  "sell.field.city": "City",
  "sell.field.district": "District",
  "sell.field.quantity": "Quantity",
  "sell.field.start_price": "Start price",
  "sell.field.floor_price": "Set a floor",
  "sell.field.duration": "Decay duration",
  "sell.duration.h6": "6 hours",
  "sell.duration.h6.drop_only": "6 hours, Drop only",
  "sell.duration.h24": "24 hours",
  "sell.duration.d3": "3 days",
  "sell.duration.d7": "7 days",
  "sell.start.drop": "Drop {when}",
  "sell.start.drop.queued": "{n} items already queued for this Drop",
  "sell.start.now": "Start now",
  "sell.preview": "Buyers will see it like this (no floor shown)",
  "sell.publish": "Publish",
  "sell.published.drop": "Queued. Goes live {when}.",
  "sell.published.now": "Published. The price is falling.",
  "sell.cap_reached": "You have 30 listings live or scheduled. End some before adding more.",
  "sell.drafts_cap": "Up to 10 drafts.",
  "sell.need_invite": "You need an invite from a seller to list.",
  "sell.lower_floor": "Lower the floor",
  "sell.cancel_listing": "Cancel",
  "sell.cancel_listing.confirm":
    "Cancel this live listing? Remaining stock is voided; existing purchases still stand.",
  "sell.relist": "Relist",
  "sell.tabs.active": "Live",
  "sell.tabs.scheduled": "Scheduled (Drop)",
  "sell.tabs.drafts": "Drafts",
  "sell.tabs.ended": "Ended",
  "sell.ladder": "Alert ladder",
  "sell.ladder.hint": "Buyers are waiting at these levels (anonymised).",
  "sell.ladder.min": "Shown once there are 3 or more alerts.",

  "home.guest_cta": "Want to sell? Ask a seller for an invite.",
  "browse.tab.live": "Live now",
  "browse.tab.next_drop": "Next Drop",
  "browse.sort.default": "Ending soonest",
  "browse.sort.newest": "Newest",
  "browse.sort.price_asc": "Price low → high",
  "browse.sort.price_desc": "Price high → low",
  "browse.results": "{n} items",
  "empty.browse": "The floor is quiet. Next Drop Saturday 20:00.",
  "empty.watching":
    "Nothing watched yet. Set an alert to be pinged when the price hits your number.",
  "empty.listings": "Nothing listed yet. Got something collecting dust? Let the market price it.",
  "empty.purchases": "No purchases yet.",
  "empty.sales": "No sales yet.",
  "empty.notifications": "No notifications yet.",
  "empty.search": "Nothing matches “{q}”.",

  "report.title": "Report listing",
  "report.reason.prohibited": "Prohibited item",
  "report.reason.counterfeit": "Counterfeit",
  "report.reason.misleading": "Misleading",
  "report.reason.spam": "Spam",
  "report.reason.other": "Other",
  "report.sent": "Report sent.",

  "notif.title": "Notifications",
  "notif.mark_all": "Mark all as read",
  "notif.purchase_received.title": "@{buyer} bought {title}",
  "notif.purchase_received.body": "{qty} × {price}. Share contact within 24 h.",
  "notif.contact_shared.title": "@{seller} shared contact",
  "notif.contact_shared.body": "Meet up and hand over the deal code when you receive the item.",
  "notif.alert_reached.title": "{title} reached your price",
  "notif.alert_reached.body": "{price}. Buy before someone else does.",
  "notif.watch_ending_soon.title": "{title} is ending soon",
  "notif.watch_ending_soon.body": "Under an hour left. Now {price}.",
  "notif.watch_sold_out.title": "{title} sold",
  "notif.watch_sold_out.body": "An item you were watching just sold.",
  "notif.watch_floor_lowered.title": "Seller lowered the floor",
  "notif.watch_floor_lowered.body": "{title} will fall further.",
  "notif.done_requested.title": "@{other} marked it done",
  "notif.done_requested.body": "Confirm or report a problem within 48 h.",
  "notif.purchase_completed.title": "Deal completed",
  "notif.purchase_completed.body": "{title} · {price}",
  "notif.purchase_cancelled.title": "Purchase cancelled",
  "notif.purchase_cancelled.body": "{title} · {reason}",
  "notif.problem_filed.title": "@{other} reported a problem",
  "notif.problem_filed.body": "{reason}. You can reply once.",
  "notif.problem_replied.title": "@{other} replied",
  "notif.problem_replied.body": "An admin will review it.",
  "notif.problem_resolved.title": "Problem resolved",
  "notif.problem_resolved.body": "{note}",
  "notif.strike_issued.title": "You received a strike",
  "notif.strike_issued.body": "{reason}. Strikes expire after 90 days. Three pause your account.",
  "notif.account_suspended.title": "Account paused",
  "notif.account_suspended.body": "3 strikes in 90 days. An admin will review.",
  "notif.invite_requested.title": "@{buyer} asked for a seller invite",
  "notif.invite_requested.body": "Accepting uses one of your invite codes.",
  "notif.invite_granted.title": "You're a seller now",
  "notif.invite_granted.body": "@{seller} invited you. List your first item.",
  "notif.open": "Open",
  "notif.drop_reminder.title": "Drop starts in 1 hour",
  "notif.drop_reminder.body": "{n} items go live at 20:00.",
  "notif.drop_start.title": "The Drop has started",
  "notif.drop_start.body": "{n} items just went live. Prices are falling.",
  "notif.admin_suspended.title": "@{handle} was just paused",
  "notif.admin_suspended.body": "{n} active strikes. Lift or ban in Admin.",

  "settings.title": "Settings",
  "settings.profile": "Profile",
  "settings.phone": "Phone",
  "settings.phone.change": "Change phone number",
  "settings.devices": "Devices",
  "settings.devices.this": "This device",
  "settings.devices.revoke": "Log out",
  "settings.devices.revoke_all": "Log out all other devices",
  "settings.notifications": "Notifications",
  "settings.telegram.link": "Link Telegram",
  "settings.telegram.linked": "Telegram linked",
  "settings.telegram.unlink": "Unlink",
  "settings.drop_reminders": "Weekly Drop reminders",
  "settings.become_seller": "Become a seller",
  "settings.invite.enter": "Enter invite code",
  "settings.invite.redeem": "Activate",
  "settings.seller.status": "You're a seller · {n} invites left",
  "settings.invite.codes": "Your invite codes",
  "settings.invite.topup": "Topped up to 3 every Monday.",
  "settings.auto_share": "Auto-share contact",
  "settings.auto_share.hint":
    "Buyers see your number the moment they buy. Only for sellers with 3 completed sales and no strikes.",
  "settings.delete": "Delete account",
  "settings.delete.warning":
    "Live listings are cancelled, open purchases are cancelled with strikes. Data is anonymised after 14 days. This number can't register again.",
  "settings.delete.confirm": "Delete permanently",
  "settings.export.hint": "Need a copy of your data? Email us.",
  "settings.email": "Email (optional)",
  "settings.city": "City",
  "settings.district": "District",
  "profile.as_seller": "Seller",
  "profile.as_buyer": "Buyer",
  "notif.telegram.linked": "Telegram is linked to Yabe. Notifications will arrive here.",
  "notif.telegram.stopped": "Telegram notifications are off. Turn them back on in Settings.",
  "notif.telegram.unknown": "Open Yabe → Settings → Link Telegram to get started.",
  "sms.purchase_received":
    "Yabe: @{buyer} bought {title} ({qty} x {price}). Share contact within 24h: {url}",
  "settings.invite.redeemed": "You're a seller now. List your first item.",
  "settings.invite.link": "Send this link to someone you want to invite:",
  "settings.invite.used_by": "used by @{handle}",
  "settings.saved": "Saved.",
  "settings.phone.new": "New phone number",
  "settings.phone.current": "Current number",
  "settings.devices.last_seen": "Active {when}",
  "settings.handle.cooldown": "You can change it again from {date}.",
  "settings.delete.confirm_title": "Delete account?",

  "legal.terms": "Terms of Service",
  "legal.privacy": "Privacy Policy",
  "legal.prohibited": "Prohibited Items",
  "legal.reaccept": "The terms have changed. Please read and accept to continue.",
  "legal.version": "Version {version}",

  "time.now": "just now",
  "time.minutes_ago": "{n} min ago",
  "time.hours_ago": "{n} h ago",
  "time.days_ago": "{n} d ago",
  "time.weekday.6": "Sat",
  "time.weekday.0": "Sun",

  "footer.support": "Support group",
  "footer.made_in": "Made in Saigon",
};
